import React, { useState } from 'react';
import { 
  Lock, 
  Check, 
  ArrowRight, 
  ShieldCheck, 
  Info, 
  Database, 
  Cloud,
  Loader2,
  Sparkles,
  CreditCard
} from 'lucide-react';
import { db, doc, setDoc, serverTimestamp } from '../firebase';
import { useAuth } from '../AuthContext';
import { useSavedSolicitations } from '../SavedSolicitationsContext';
import { Card, Button, cn } from './SaaSUIHelper';

interface SubscriptionGatedViewProps {
  featureName: string;
  requiredTier: 'pro' | 'enterprise';
  icon: 'database' | 'workspace';
}

export const SubscriptionGatedView: React.FC<SubscriptionGatedViewProps> = ({ 
  featureName, 
  requiredTier, 
  icon 
}) => {
  const { user, vendorProfile } = useAuth();
  const { addToast } = useSavedSolicitations();
  const [checkoutActive, setCheckoutActive] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState<'form' | 'processing' | 'receipt'>('form');
  const [paypalEmail, setPaypalEmail] = useState('');
  const [paypalPassword, setPaypalPassword] = useState('');

  const tierPrices = {
    pro: { name: 'Professional Plan', price: '$149/mo', id: 'pro' },
    enterprise: { name: 'Enterprise Plan', price: '$499/mo', id: 'enterprise' }
  };

  const selectedTierInfo = tierPrices[requiredTier];

  const handlePayPalUpgrade = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!paypalEmail) {
      alert('Please provide your PayPal email.');
      return;
    }
    if (!user) return;

    setCheckoutStep('processing');

    // Simulate merchant payment gateway routing
    setTimeout(async () => {
      try {
        const updatedProfile = {
          ...(vendorProfile || {}),
          uid: user.uid,
          subscriptionTier: requiredTier,
          paypalEmail: paypalEmail,
          paymentStatus: 'active',
          updatedAt: serverTimestamp()
        };

        await setDoc(doc(db, 'vendors', user.uid), updatedProfile, { merge: true });
        
        addToast({
          title: 'Upgrade Successful!',
          message: `Your account has been upgraded to ${selectedTierInfo.name} via PayPal transaction secure checkout.`,
          type: 'success'
        });
        
        setCheckoutStep('receipt');
      } catch (err) {
        console.error("Failed to upgrade via PayPal:", err);
        setCheckoutStep('form');
        alert("An error occurred during transaction routing. Please try again.");
      }
    }, 2500);
  };

  return (
    <div className="max-w-3xl mx-auto py-12 px-4" id="subscription-gate-root">
      {!checkoutActive ? (
        <Card className="p-8 text-center space-y-6 border-indigo-150 shadow-md bg-white">
          <div className="flex justify-center">
            <div className="relative">
              <div className="w-16 h-16 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center shadow-inner">
                {icon === 'database' ? <Database className="w-8 h-8" /> : <Cloud className="w-8 h-8" />}
              </div>
              <div className="absolute -bottom-1 -right-1 bg-indigo-600 text-white p-1.5 rounded-lg shadow-sm border border-white">
                <Lock className="w-3.5 h-3.5" />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <span className="text-[10px] font-black tracking-widest text-indigo-600 bg-indigo-50 border border-indigo-100 px-3 py-1 rounded-full uppercase">
              Paid Membership Feature
            </span>
            <h2 className="text-xl font-extrabold text-gray-950 tracking-tight">
              Unlock the {featureName}
            </h2>
            <p className="text-xs text-gray-500 max-w-md mx-auto leading-relaxed">
              Your current account tier does not have permission to access this module. 
              Upgrade to the <span className="font-bold text-gray-900">{selectedTierInfo.name}</span> to instantly activate this membership benefit.
            </p>
          </div>

          <div className="p-4 bg-slate-50 border border-slate-100 rounded-xl max-w-md mx-auto text-left space-y-3">
            <h4 className="text-xs font-bold text-gray-800 flex items-center gap-1.5">
              <Sparkles className="w-4 h-4 text-indigo-500" />
              What is included in the {selectedTierInfo.name}?
            </h4>
            <ul className="space-y-2 text-[11px] text-gray-600">
              {requiredTier === 'pro' ? (
                <>
                  <li className="flex gap-2 items-start">
                    <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                    <span>Real-time crawler sync across CA, TX, and NY portals</span>
                  </li>
                  <li className="flex gap-2 items-start">
                    <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                    <span>Unlimited AI Bid proposal responses and summaries</span>
                  </li>
                  <li className="flex gap-2 items-start">
                    <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                    <span>Full Google Docs / Google Drive automated file sync</span>
                  </li>
                </>
              ) : (
                <>
                  <li className="flex gap-2 items-start">
                    <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                    <span>All Pro plan benefits with dedicated custom crawlers</span>
                  </li>
                  <li className="flex gap-2 items-start">
                    <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                    <span>Gemini API dedicated custom throughput allocations</span>
                  </li>
                </>
              )}
            </ul>
          </div>

          <div className="flex flex-col sm:flex-row justify-center gap-3 pt-2">
            <button 
              id="btn-gate-checkout"
              onClick={() => { setCheckoutActive(true); setCheckoutStep('form'); }}
              className="px-6 py-3 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-md shadow-indigo-100 transition-all cursor-pointer flex items-center justify-center gap-1.5"
            >
              Upgrade with PayPal Secure Checkout
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </Card>
      ) : (
        <Card className="max-w-md mx-auto overflow-hidden bg-white border border-gray-150 shadow-xl" id="paypal-active-checkout">
          {/* PayPal Banner Accent */}
          <div className="bg-[#003087] px-6 py-4 flex justify-between items-center text-white">
            <div className="flex items-center gap-1.5">
              <span className="font-black italic text-lg tracking-tight text-white flex items-center">
                Pay
                <span className="text-[#0079C1]">Pal</span>
              </span>
              <span className="text-[10px] font-bold tracking-widest uppercase text-slate-300 border-l border-slate-500 pl-2.5 ml-2.5">
                Secure Portal
              </span>
            </div>
            <button 
              onClick={() => setCheckoutActive(false)}
              className="text-slate-300 hover:text-white transition-colors cursor-pointer"
            >
              <XIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-5">
            <div className="p-4 bg-slate-50 border border-slate-150 rounded-xl flex justify-between items-center text-xs">
              <div>
                <p className="text-[10px] text-gray-400 font-bold uppercase">SECURE ORDER SUMMARY</p>
                <h4 className="font-extrabold text-gray-900 mt-0.5">{selectedTierInfo.name} Subscription</h4>
              </div>
              <span className="font-black text-indigo-600 bg-indigo-50 border border-indigo-100 px-2.5 py-1 rounded-lg">
                {selectedTierInfo.price}
              </span>
            </div>

            {checkoutStep === 'form' && (
              <form onSubmit={handlePayPalUpgrade} className="space-y-4">
                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-gray-600">PayPal Account Email</label>
                  <input 
                    id="gate-paypal-email"
                    type="email" 
                    required
                    placeholder="your-paypal-account@company.com"
                    value={paypalEmail}
                    onChange={(e) => setPaypalEmail(e.target.value)}
                    className="w-full text-xs p-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-[#003087] outline-none transition-all"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[11px] font-bold text-gray-600">Password</label>
                  <input 
                    id="gate-paypal-password"
                    type="password" 
                    required
                    placeholder="••••••••"
                    value={paypalPassword}
                    onChange={(e) => setPaypalPassword(e.target.value)}
                    className="w-full text-xs p-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-[#003087] outline-none transition-all"
                  />
                </div>

                <div className="flex gap-2 items-start text-[10px] text-gray-400">
                  <Info className="w-4 h-4 text-gray-400 shrink-0" />
                  <span>Secure automated simulated checkout. The sandbox triggers instantaneous billing profiles on your active Firestore record.</span>
                </div>

                <div className="pt-2 flex gap-2">
                  <button 
                    type="button"
                    onClick={() => setCheckoutActive(false)}
                    className="flex-1 py-3 text-xs font-bold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-xl cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button 
                    id="btn-gate-submit-upgrade"
                    type="submit"
                    className="flex-1 py-3 text-xs font-bold text-white bg-[#0079C1] hover:bg-[#005ea6] rounded-xl cursor-pointer shadow-xs text-center"
                  >
                    Pay & Unlock Now
                  </button>
                </div>
              </form>
            )}

            {checkoutStep === 'processing' && (
              <div className="py-12 flex flex-col items-center justify-center space-y-4">
                <Loader2 className="w-10 h-10 text-[#003087] animate-spin" />
                <div className="text-center space-y-1">
                  <h4 className="text-xs font-bold text-gray-900">Routing PayPal Gateway...</h4>
                  <p className="text-[10px] text-gray-400">Executing sandbox transactions and authorizing premium scopes...</p>
                </div>
              </div>
            )}

            {checkoutStep === 'receipt' && (
              <div className="space-y-5 text-center">
                <div className="w-12 h-12 bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-full flex items-center justify-center mx-auto">
                  <ShieldCheck className="w-6 h-6" />
                </div>

                <div className="space-y-1">
                  <h4 className="text-sm font-bold text-gray-900">Subscription Upgraded Successfully!</h4>
                  <p className="text-[10px] text-gray-400">Your account tier has been updated to <span className="font-bold text-indigo-600">{requiredTier.toUpperCase()}</span>.</p>
                </div>

                <div className="p-3.5 bg-emerald-50 border border-emerald-100 rounded-xl text-left space-y-1.5 text-[11px] text-emerald-950">
                  <p className="flex justify-between">
                    <span>Authorized Transaction:</span>
                    <span className="font-mono">#PAYPAL-{Math.floor(100000 + Math.random() * 900000)}</span>
                  </p>
                  <p className="flex justify-between">
                    <span>Account upgraded:</span>
                    <span>{user?.email}</span>
                  </p>
                </div>

                <button 
                  id="btn-gate-done"
                  onClick={() => setCheckoutActive(false)}
                  className="w-full py-3.5 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl cursor-pointer"
                >
                  Enter Paid Membership Area
                </button>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};

// Simple inline XIcon helper
const XIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    fill="none" 
    viewBox="0 0 24 24" 
    strokeWidth={2} 
    stroke="currentColor" 
    className="w-5 h-5"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);
